import moment from 'moment';
import React, { useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { touchHitArea } from '@/components/ui/touch-target';
import {
	FormControl,
	FormField,
	FormItem,
	FormMessage,
} from '@/components/ui/form';
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/components/ui/popover';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { MultiUserSelector } from '@/components/runner/utils/MultiUserSelector';
import { addDepartmentToAssignees } from '@/components/event-calendar/utils/departments';
import { NotificationSettingsField } from '@/components/notifications/NotificationSettingsField';
import {
	DEFAULT_NOTIFICATIONS,
	readNotifications,
} from '@/components/notifications/notificationSettings';
import {
	CalendarEventType,
	EventCalendarTranslations,
} from '@/components/event-calendar/types';
import { User as UserDTO } from '@/types';
import CustomerSearch from '@/components/utils/DragAndDropUploader/CustomerSearch';
import formatPhone from '@/utils/formatPhoneForUser';
import TalkButton from '@/components/ui/custom/TalkButton';
import TimeInputController from './TimeInputController';

interface JobEventFormProps {
	form: UseFormReturn<CalendarEventType>;
	translations: EventCalendarTranslations;
	event?: Partial<CalendarEventType>;
	/** Regular events link a department; jobs have no department, so picking one adds its members */
	linksDepartment?: boolean;
}

const TITLE_MAX_LENGTH = 200;
const DESCRIPTION_MAX_LENGTH = 1000;
const ALL_DAY_START = '00:00';
const ALL_DAY_END = '23:59';
const MIN_DATE = new Date('1900-01-01');
const MAX_DATE = new Date('2100-12-31');
const MAX_DURATION_MS = 10 * 365 * 24 * 60 * 60 * 1000;

const BORDERLESS_FIELD =
	'rounded-none border-0 bg-transparent px-4 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0';
// The date and time pills are 44px touch targets on phones and touch screens.
// On phones they never get wider than the row, so with very large text a date
// wraps onto two lines instead of running out of the form.
const PILL =
	'h-8 rounded-md border-0 bg-background px-2.5 text-sm font-normal shadow-sm hover:bg-background/70 dark:bg-background/60 max-md:h-11 max-md:max-w-full touch:h-11';
const DATE_PILL = `${PILL} max-md:h-auto max-md:min-h-11 max-md:whitespace-normal`;
const TIME_PILL = `w-[8.5rem] max-md:w-28 ${PILL}`;
// [&>div] is TimeInputController's wrapper, which would otherwise keep the time pill's full width
const PILL_ITEM =
	'flex flex-col items-end space-y-1 max-md:max-w-full max-md:[&>div]:max-w-full';

const isNearLimit = (length: number, max: number) => length >= max * 0.8;

export const Group: React.FC<{ children: React.ReactNode }> = ({ children }) => (
	<div className="divide-y divide-border/60 overflow-hidden rounded-xl bg-muted/60 focus-within:ring-1 focus-within:ring-ring/40">
		{children}
	</div>
);

// How a row's fields give way on phones when they don't fit beside the label
// (narrow screens, larger text). A switch always stays beside its label.
const ROW_WRAP = {
	// The date and time go under the label, lined up on the left, and the
	// time under the date when the two don't fit on one line. Sized to the
	// pills, so the row only keeps them beside the label when they all fit.
	pills: {
		row: ' max-md:flex-wrap max-md:justify-between max-md:gap-y-1.5',
		fields: ' max-md:max-w-full max-md:flex-initial max-md:flex-wrap max-md:justify-start max-md:gap-y-1.5',
	},
	// A search or picker goes under the label once it has less than 9rem beside it
	field: {
		row: ' max-md:flex-wrap max-md:gap-y-1.5',
		fields: ' max-md:min-w-[min(100%,9rem)]',
	},
};

export const Row: React.FC<{
	label: string;
	htmlFor?: string;
	wrap?: keyof typeof ROW_WRAP;
	children: React.ReactNode;
}> = ({ label, htmlFor, wrap, children }) => (
	<div
		className={`flex min-h-11 items-center gap-3 px-4 py-1.5${
			wrap ? ROW_WRAP[wrap].row : ''
		}`}
	>
		<Label
			htmlFor={htmlFor}
			className="w-24 shrink-0 text-sm font-normal max-md:w-[4.5rem]"
		>
			{label}
		</Label>
		<div
			className={`flex min-w-0 flex-1 items-center justify-end gap-2${
				wrap ? ROW_WRAP[wrap].fields : ''
			}`}
		>
			{children}
		</div>
	</div>
);

const CharacterCount: React.FC<{ length: number; max: number }> = ({
	length,
	max,
}) =>
	isNearLimit(length, max) ? (
		<p className="px-4 pb-2 text-right text-xs text-muted-foreground">
			{length}/{max}
		</p>
	) : null;

export const JobEventForm: React.FC<JobEventFormProps> = ({
	form,
	translations,
	event,
	linksDepartment = false,
}) => {
	const [startDateOpen, setStartDateOpen] = useState(false);
	const [endDateOpen, setEndDateOpen] = useState(false);

	const titleValue = form.watch('title') || '';
	const descriptionValue = form.watch('description') || '';
	const isAllDay =
		form.watch('startTime') === ALL_DAY_START &&
		form.watch('endTime') === ALL_DAY_END;

	// Jobs saved before notifications stay silent; events get the defaults
	const notificationKind = linksDepartment ? 'event' : 'job';

	const handleAllDayChange = (allDay: boolean) => {
		form.setValue('startTime', allDay ? ALL_DAY_START : '09:00');
		form.setValue('endTime', allDay ? ALL_DAY_END : '10:00');
	};

	const handleStartDateSelect = (
		date: Date | undefined,
		onChange: (date: Date) => void
	) => {
		if (!date) return;

		if (date < MIN_DATE) {
			toast.error(
				translations.validations.startDateTooEarly ||
					'Start date cannot be before 1900'
			);
			return;
		}

		if (date > MAX_DATE) {
			toast.error(
				translations.validations.startDateTooLate ||
					'Start date cannot be after 2100'
			);
			return;
		}

		onChange(date);
		setStartDateOpen(false);
	};

	const handleEndDateSelect = (
		date: Date | undefined,
		onChange: (date: Date) => void
	) => {
		if (!date) return;

		const startDate = form.getValues('startDate');
		const startDay = new Date(
			startDate.getFullYear(),
			startDate.getMonth(),
			startDate.getDate()
		);
		const selectedDay = new Date(
			date.getFullYear(),
			date.getMonth(),
			date.getDate()
		);

		if (date < MIN_DATE) {
			toast.error(
				translations.validations.endDateTooEarly ||
					'End date cannot be before 1900'
			);
			return;
		}

		if (date > MAX_DATE) {
			toast.error(
				translations.validations.endDateTooLate ||
					'End date cannot be after 2100'
			);
			return;
		}

		if (date.getTime() - startDate.getTime() > MAX_DURATION_MS) {
			toast.error(
				translations.validations.eventDurationTooLong ||
					'Event duration cannot exceed 10 years'
			);
			return;
		}

		if (selectedDay < startDay) {
			toast.error(translations.errorEndDateBeforeStart);
			return;
		}

		onChange(date);
		setEndDateOpen(false);
	};

	return (
		<div className="space-y-3">
			<Group>
				<FormField
					control={form.control}
					name="title"
					render={({ field }) => (
						<FormItem className="space-y-0">
							<FormControl>
								<Input
									placeholder={translations.eventTitle}
									aria-label={translations.eventTitle}
									{...field}
									autoFocus={!event}
									maxLength={TITLE_MAX_LENGTH}
									className={`h-12 text-base font-medium ${BORDERLESS_FIELD}`}
								/>
							</FormControl>
							<CharacterCount
								length={titleValue.length}
								max={TITLE_MAX_LENGTH}
							/>
							<FormMessage className="px-4 pb-2" />
						</FormItem>
					)}
				/>
			</Group>

			<Group>
				<Row
					label={translations.timePresetAllDay}
					htmlFor="event-all-day"
				>
					<Switch
						id="event-all-day"
						checked={isAllDay}
						onCheckedChange={handleAllDayChange}
						// Tablets keep the small desktop switch, so give it a 44px hit area there too
						className={touchHitArea(undefined, { coarse: true })}
					/>
				</Row>

				<Row label="Starts" wrap="pills">
					<FormField
						control={form.control}
						name="startDate"
						render={({ field }) => (
							<FormItem className={PILL_ITEM}>
								<Popover
									open={startDateOpen}
									onOpenChange={setStartDateOpen}
								>
									<PopoverTrigger asChild>
										<FormControl>
											<Button
												type="button"
												variant="ghost"
												aria-label={
													translations.eventDate
												}
												className={DATE_PILL}
											>
												{field.value
													? moment(
															field.value
														).format('ll')
													: translations.pickADate}
											</Button>
										</FormControl>
									</PopoverTrigger>
									<PopoverPrimitive.Portal>
										<PopoverContent className="w-auto p-0">
											<Calendar
												mode="single"
												selected={field.value}
												onSelect={(date) =>
													handleStartDateSelect(
														date,
														field.onChange
													)
												}
												fromDate={MIN_DATE}
												toDate={MAX_DATE}
												defaultMonth={
													field.value || new Date()
												}
											/>
										</PopoverContent>
									</PopoverPrimitive.Portal>
								</Popover>
								<FormMessage className="text-xs" />
							</FormItem>
						)}
					/>
					{!isAllDay && (
						<FormField
							control={form.control}
							name="startTime"
							render={({ field }) => (
								<FormItem className={PILL_ITEM}>
									<FormControl>
										<TimeInputController
											aria-label={
												translations.eventStartTime
											}
											value={field.value}
											onChange={field.onChange}
											className={TIME_PILL}
										/>
									</FormControl>
									<FormMessage className="text-xs" />
								</FormItem>
							)}
						/>
					)}
				</Row>

				<Row label="Ends" wrap="pills">
					<FormField
						control={form.control}
						name="endDate"
						render={({ field }) => (
							<FormItem className={PILL_ITEM}>
								<Popover
									open={endDateOpen}
									onOpenChange={setEndDateOpen}
								>
									<PopoverTrigger asChild>
										<FormControl>
											<Button
												type="button"
												variant="ghost"
												aria-label={
													translations.eventEndDate
												}
												className={DATE_PILL}
											>
												{field.value
													? moment(
															field.value
														).format('ll')
													: translations.pickADate}
											</Button>
										</FormControl>
									</PopoverTrigger>
									<PopoverPrimitive.Portal>
										<PopoverContent className="w-auto p-0">
											<Calendar
												mode="single"
												selected={field.value}
												onSelect={(date) =>
													handleEndDateSelect(
														date,
														field.onChange
													)
												}
												fromDate={form.getValues(
													'startDate'
												)}
												toDate={MAX_DATE}
												defaultMonth={
													field.value || new Date()
												}
											/>
										</PopoverContent>
									</PopoverPrimitive.Portal>
								</Popover>
								<FormMessage className="text-xs" />
							</FormItem>
						)}
					/>
					{!isAllDay && (
						<FormField
							control={form.control}
							name="endTime"
							render={({ field }) => (
								<FormItem className={PILL_ITEM}>
									<FormControl>
										<TimeInputController
											aria-label={
												translations.eventEndTime
											}
											value={field.value}
											onChange={field.onChange}
											className={TIME_PILL}
										/>
									</FormControl>
									<FormMessage className="text-xs" />
								</FormItem>
							)}
						/>
					)}
				</Row>
			</Group>

			<Group>
				{/* An existing job's customer is changed from the job itself; regular events own theirs */}
				{(!event?.id ||
					event.calendarEvent ||
					event.job?.customer?.name) && (
					<FormField
						control={form.control}
						name="job"
						render={({ field }) => {
							const customer = field.value?.customer;

							return (
								<Row label="Customer" wrap="field">
									{customer ? (
										<>
											<span className="min-w-0 truncate text-right text-sm">
												{customer.name || 'Customer'}
												{customer.phone && (
													<span className="text-muted-foreground">
														{' '}
														·{' '}
														{formatPhone(
															customer.phone
														)}
													</span>
												)}
											</span>
											{(!event?.id || event.calendarEvent) && (
												<Button
													type="button"
													variant="ghost"
													size="sm"
													className="h-8 px-2 text-muted-foreground"
													onClick={() =>
														field.onChange({
															...field.value,
															customer: undefined,
														})
													}
												>
													Remove
												</Button>
											)}
										</>
									) : (
										<div className="w-full">
											<CustomerSearch
												minimal
												handleSelectCustomer={(selected: {
													_id: string;
													name?: string | null;
													phone?: string | null;
													email?: string | null;
												}) =>
													field.onChange({
														...(field.value || {
															_id: '',
															_project: '',
															_assignees: [],
														}),
														// Only the details shown here; empty ones are left out
														customer: {
															_id: selected._id,
															name:
																selected.name ||
																undefined,
															phone:
																selected.phone ||
																undefined,
															email:
																selected.email ||
																undefined,
														},
													})
												}
												disabled={false}
											/>
										</div>
									)}
								</Row>
							);
						}}
					/>
				)}

				<FormField
					control={form.control}
					name="job"
					render={({ field }) => (
						<Row label="Assignees" htmlFor="assignees" wrap="field">
							<MultiUserSelector
								test-id="assignees-selector"
								side="top"
								// A 44px hit area on phones and touch screens, keeping the row's height
								className={`w-full justify-end gap-2 border-0 bg-transparent px-0 font-normal shadow-none hover:bg-transparent ${touchHitArea(undefined, { coarse: true })}`}
								onSelectUser={(user: UserDTO) => {
									const currentJob = field.value || {
										_id: '',
										_project: '',
										_assignees: [],
									};
									const currentAssignees =
										currentJob._assignees || [];
									const updatedAssignees =
										currentAssignees.includes(user._id)
											? currentAssignees.filter(
													(id) => id !== user._id
												)
											: [...currentAssignees, user._id];

									field.onChange({
										...currentJob,
										_assignees: updatedAssignees,
									});
								}}
								onSelectDepartment={(department) => {
									const currentJob = field.value || {
										_id: '',
										_project: '',
										_assignees: [],
									};
									if (linksDepartment) {
										field.onChange({
											...currentJob,
											_department:
												currentJob._department === department._id
													? null
													: department._id,
										});
										return;
									}
									field.onChange({
										...currentJob,
										_assignees: addDepartmentToAssignees(
											(currentJob._assignees || []).filter(
												(id): id is string => !!id
											),
											department
										),
									});
								}}
								_selectedDepartment={
									linksDepartment ? field.value?._department : undefined
								}
								_selectedUsers={field.value?._assignees || []}
								onCloseMenu={() => {
									/* Optional close handler */
								}}
							/>
						</Row>
					)}
				/>

				<FormField
					control={form.control}
					name="job"
					render={({ field }) => (
						<Row label="Notify" htmlFor="event-notify" wrap="field">
							<NotificationSettingsField
								id="event-notify"
								kind={notificationKind}
								value={
									!event?.id && !field.value?.notifications
										? DEFAULT_NOTIFICATIONS
										: readNotifications(field.value, notificationKind)
								}
								onChange={(notifications) =>
									field.onChange({
										...(field.value || {
											_id: '',
											_project: '',
											_assignees: [],
										}),
										notifications,
									})
								}
								allDay={isAllDay}
								assigneeIds={field.value?._assignees || []}
								departmentId={
									linksDepartment ? field.value?._department : undefined
								}
								creatorId={field.value?._createdByUser}
								isNew={!event?.id}
							/>
						</Row>
					)}
				/>
			</Group>

			<Group>
				<FormField
					control={form.control}
					name="description"
					render={({ field }) => (
						<FormItem className="space-y-0">
							<div className="relative">
								<FormControl>
									<Textarea
										placeholder={
											translations.eventDescription
										}
										aria-label={
											translations.eventDescription
										}
										{...field}
										maxLength={DESCRIPTION_MAX_LENGTH}
										className={`min-h-24 py-3 pr-12 ${BORDERLESS_FIELD}`}
									/>
								</FormControl>
								<div className="absolute bottom-1.5 right-1.5">
									<TalkButton
										value={field.value || ''}
										maxLength={DESCRIPTION_MAX_LENGTH}
										onChange={field.onChange}
									/>
								</div>
							</div>
							<CharacterCount
								length={descriptionValue.length}
								max={DESCRIPTION_MAX_LENGTH}
							/>
							<FormMessage className="px-4 pb-2" />
						</FormItem>
					)}
				/>
			</Group>
		</div>
	);
};
